import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Switch,
    Typography
} from "@mui/material";
import { makeStyles } from "@Compat/makeStyles";
import { alpha, Theme } from "@mui/material/styles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons/faXmark";
import { useTranslation } from "react-i18next";
import { ConsentCategories, DENY_ALL, GRANT_ALL, getConsent } from "../../core/consent";

// The consent UI shows on the marketing site and in the app, under any
// theme: its colours come from the active palette, not the old cobalt.
const accentGradient = (theme: Theme) =>
    `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`;

const useStyles = makeStyles((theme) => ({
    paper: {
        borderRadius: 28,
        maxWidth: 560,
        width: "100%",
        background:
            alpha(theme.palette.background.paper, theme.palette.mode === "dark" ? 0.96 : 0.98),
        border: `1px solid ${alpha(theme.palette.primary.main, 0.13)}`,
        [theme.breakpoints.down("sm")]: {
            borderRadius: 20,
            margin: theme.spacing(1)
        }
    },
    title: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        fontWeight: 800,
        paddingBottom: theme.spacing(1)
    },
    intro: {
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(2)
    },
    category: {
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: theme.spacing(2),
        padding: theme.spacing(2, 0),
        borderTop: `1px solid ${theme.palette.divider}`
    },
    categoryText: {
        flex: 1
    },
    categoryName: {
        fontWeight: 700
    },
    categoryDesc: {
        color: theme.palette.text.secondary,
        fontSize: "0.88rem",
        marginTop: theme.spacing(0.5)
    },
    actions: {
        padding: theme.spacing(2, 3, 3),
        gap: theme.spacing(1.5),
        flexWrap: "wrap"
    },
    pill: {
        "&&": {
            borderRadius: 999,
            textTransform: "none",
            fontWeight: 700,
            padding: theme.spacing(1, 3)
        }
    },
    acceptPill: {
        "&&": {
            color: theme.palette.primary.contrastText,
            background: accentGradient(theme),
            boxShadow: `0 12px 28px ${alpha(theme.palette.primary.main, 0.34)}`,
            "&:hover": { background: accentGradient(theme), boxShadow: `0 16px 34px ${alpha(theme.palette.primary.main, 0.46)}` }
        }
    }
}));

interface CookiePreferencesDialogProps {
    open: boolean;
    onClose: () => void;
    onSave: (categories: ConsentCategories) => void;
}

const CookiePreferencesDialog: React.FC<CookiePreferencesDialogProps> = ({ open, onClose, onSave }) => {
    const classes = useStyles();
    const { t } = useTranslation("CookieConsent");
    const [draft, setDraft] = useState<ConsentCategories>({ ...DENY_ALL });

    // Seed the toggles from the currently-stored decision each time it opens.
    useEffect(() => {
        if (open) {
            setDraft({ ...getConsent() });
        }
    }, [open]);

    const toggle = (key: keyof ConsentCategories) => (_: unknown, checked: boolean) => {
        setDraft((prev) => ({ ...prev, [key]: checked }));
    };

    const categories: Array<{ key: keyof ConsentCategories; locked?: boolean }> = [
        { key: "analytics" },
        { key: "marketing" },
        { key: "preferences" }
    ];

    return (
        <Dialog open={open} onClose={onClose} PaperProps={{ className: classes.paper }} maxWidth="sm" fullWidth>
            <DialogTitle component="div" className={classes.title}>
                {t("prefs.title")}
                <IconButton size="small" onClick={onClose} aria-label={t("close")}>
                    <FontAwesomeIcon icon={faXmark} style={{ fontSize: 20 }} />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Typography variant="body2" className={classes.intro}>
                    {t("prefs.intro")}
                </Typography>

                {/* Necessary — always on, cannot be disabled. */}
                <Box className={classes.category}>
                    <div className={classes.categoryText}>
                        <Typography className={classes.categoryName}>{t("cat.necessary.name")}</Typography>
                        <Typography className={classes.categoryDesc}>{t("cat.necessary.desc")}</Typography>
                    </div>
                    <Switch checked disabled color="primary" inputProps={{ "aria-label": t("cat.necessary.name") }} />
                </Box>

                {categories.map(({ key }) => (
                    <Box key={key} className={classes.category}>
                        <div className={classes.categoryText}>
                            <Typography className={classes.categoryName}>{t(`cat.${key}.name`)}</Typography>
                            <Typography className={classes.categoryDesc}>{t(`cat.${key}.desc`)}</Typography>
                        </div>
                        <Switch
                            checked={draft[key]}
                            onChange={toggle(key)}
                            color="primary"
                            inputProps={{ "aria-label": t(`cat.${key}.name`) }}
                        />
                    </Box>
                ))}
            </DialogContent>
            <DialogActions className={classes.actions}>
                <Button className={classes.pill} onClick={() => onSave({ ...DENY_ALL })}>
                    {t("rejectAll")}
                </Button>
                <Box flexGrow={1} />
                <Button className={classes.pill} variant="outlined" onClick={() => onSave(draft)}>
                    {t("savePrefs")}
                </Button>
                <Button className={`${classes.pill} ${classes.acceptPill}`} onClick={() => onSave({ ...GRANT_ALL })}>
                    {t("acceptAll")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CookiePreferencesDialog;
